import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  Stack,
  Card,
  CardContent
} from "@mui/material";
import {
  Add,
  Remove,
  CloudUpload,
  Image as ImageIcon,
  VideoLibrary,
  Close
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import KeywordsInput from "../KeywordsInput";
import CropModalForAdd from "./CropModalForAdd";
import { getCroppedImg } from "../../../../core/utils/imageUtils"; // Import from utility

const AddProductModal = ({
  open,
  onClose,
  input,
  onChange,
  onSubmit,
  categories,
  errors,
  specificationErrors,
  keywordsError,
  isDealerPriceEnabled,
  loading,
  onSpecificationChange,
  addSpecificationRow,
  removeSpecificationRow,
  images,
  videos,
  imageErrors,
  videoErrors,
  onImageSelect,
  onVideoSelect,
  onRemoveImage,
  onRemoveVideo,
  validateForm,
  touched,
  setImages,
  setImageErrors
}) => {
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [croppingQueue, setCroppingQueue] = useState([]);
  const [currentCropIndex, setCurrentCropIndex] = useState(0);

  const handleSubmit = (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (isValid) {
      onSubmit(e);
    }
  };

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    const currentCount = images.length;
    const totalAfterAdd = currentCount + imageFiles.length;

    event.target.value = "";

    if (imageFiles.length === 0) {
      return;
    }

    if (totalAfterAdd > 4) {
      return;
    }

    const processedFiles = imageFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      cropped: false,
      croppedBlob: null,
      cropState: null
    }));

    setCroppingQueue(processedFiles);
    setCurrentCropIndex(0);
    setCropModalOpen(true);
  };

  const handleSaveCurrentCrop = async (index, cropPixels, cropState) => {
    const updatedQueue = [...croppingQueue];
    updatedQueue[index].cropState = cropState;
    try {
      const croppedBlob = await getCroppedImg(updatedQueue[index].url, cropPixels);
      updatedQueue[index].cropped = true;
      updatedQueue[index].croppedBlob = croppedBlob;
    } catch (error) {
      console.error("Error cropping image:", error);
    }
    setCroppingQueue(updatedQueue);
  };

  const handleFinishCropping = () => {
    const finalImages = croppingQueue
      .filter((img) => img.cropped && img.croppedBlob)
      .map((img) => img.croppedBlob);

    if (finalImages.length === 0) {
      return;
    }

    const totalAfterAdd = images.length + finalImages.length;
    if (totalAfterAdd > 4) {
      return;
    }

    setImages((prev) => [...prev, ...finalImages]);

    if (setImageErrors) {
      setImageErrors([]);
    }

    croppingQueue.forEach((img) => URL.revokeObjectURL(img.url));

    setCropModalOpen(false);
    setCroppingQueue([]);
    setCurrentCropIndex(0);
  };

  const handleCropNavigation = (newIndex) => {
    if (newIndex >= 0 && newIndex < croppingQueue.length) {
      setCurrentCropIndex(newIndex);
    }
  };

  const handleCropModalClose = () => {
    croppingQueue.forEach((img) => URL.revokeObjectURL(img.url));
    setCropModalOpen(false);
    setCroppingQueue([]);
    setCurrentCropIndex(0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Add New Product
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <MDBox component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
          <Stack spacing={3}>
            {/* Basic Information Section */}
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#667eea' }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Product Name"
                      name="product_name"
                      value={input.product_name || ""}
                      onChange={onChange}
                      fullWidth
                      error={touched?.product_name && !!errors.product_name}
                      helperText={touched?.product_name && errors.product_name}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#667eea' },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={touched?.parent_category_name && !!errors.parent_category_name}>
                      <InputLabel>Product Category *</InputLabel>
                      <Select
                        label="Product Category"
                        name="parent_category_name"
                        value={input.parent_category_name || ""}
                        onChange={onChange}
                        required
                        sx={{
                          height: '44px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#c4c4c4'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea'
                          }
                        }}
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.category_name}>
                            {category.category_name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched?.parent_category_name && errors.parent_category_name && (
                        <FormHelperText>{errors.parent_category_name}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      name="description"
                      value={input.description || ""}
                      onChange={onChange}
                      fullWidth
                      multiline
                      rows={4}
                      error={touched?.description && !!errors.description}
                      helperText={touched?.description && errors.description}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#667eea' },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Warranty Description (Optional)"
                      name="warranty_description"
                      value={input.warranty_description || ""}
                      onChange={onChange}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="e.g., 1-year manufacturer warranty on parts"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Pricing Section */}
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#667eea' }}>
                  Pricing & Stock
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Price"
                      name="price"
                      value={input.price || ""}
                      onChange={onChange}
                      type="number"
                      fullWidth
                      error={touched?.price && !!errors.price}
                      helperText={touched?.price && errors.price}
                      required
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>₹</Typography>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Discounted Price"
                      name="discounted_price"
                      value={input.discounted_price || ""}
                      onChange={onChange}
                      type="number"
                      fullWidth
                      error={touched?.discounted_price && !!errors.discounted_price}
                      helperText={touched?.discounted_price && errors.discounted_price}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>₹</Typography>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Discount (%)"
                      name="discount"
                      value={input.discount ?? ""}
                      type="number"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: <Typography sx={{ ml: 1, color: '#666' }}>%</Typography>
                      }}
                      sx={{ bgcolor: '#f5f5f5' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Stock Quantity"
                      name="stock_quantity"
                      value={input.stock_quantity || ""}
                      onChange={onChange}
                      type="number"
                      fullWidth
                      error={touched?.stock_quantity && !!errors.stock_quantity}
                      helperText={touched?.stock_quantity && errors.stock_quantity}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth error={touched?.gst_type && !!errors.gst_type}>
                      <InputLabel>GST Type *</InputLabel>
                      <Select
                        label="GST Type"
                        name="gst_type"
                        value={input.gst_type || ""}
                        onChange={onChange}
                        required
                        sx={{
                          height: '44px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#c4c4c4'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea'
                          }
                        }}
                      >
                        <MenuItem value="Include GST">Include GST</MenuItem>
                        <MenuItem value="No GST">No GST</MenuItem>
                      </Select>
                      {touched?.gst_type && errors.gst_type && (
                        <FormHelperText>{errors.gst_type}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  {(input.gst_type === "Include GST" || input.gst_type === "Exclude GST") && (
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="GST Percentage (%)"
                        name="gst_percentage"
                        value={input.gst_percentage || ""}
                        onChange={onChange}
                        type="number"
                        fullWidth
                        error={touched?.gst_percentage && !!errors.gst_percentage}
                        helperText={touched?.gst_percentage && errors.gst_percentage}
                        required
                        InputProps={{
                          endAdornment: <Typography sx={{ ml: 1, color: '#666' }}>%</Typography>
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Specifications Section */}
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea' }}>
                    Specifications
                  </Typography>
                  <Chip
                    label={`${input.specification?.length || 0} items`}
                    size="small"
                    color="primary"
                    sx={{ bgcolor: '#667eea' }}
                  />
                </Box>
                <Stack spacing={2}>
                  {(input.specification ?? []).map((spec, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: '#f8f9fa',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1.5
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <TextField
                            label="Title"
                            value={spec?.title || ""}
                            onChange={(e) => onSpecificationChange(index, "title", e.target.value)}
                            fullWidth
                            size="small"
                            error={!!specificationErrors[index]?.title}
                            helperText={specificationErrors[index]?.title}
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            label="Description"
                            value={spec?.desc || ""}
                            onChange={(e) => onSpecificationChange(index, "desc", e.target.value)}
                            fullWidth
                            size="small"
                            error={!!specificationErrors[index]?.desc}
                            helperText={specificationErrors[index]?.desc}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <IconButton
                              onClick={() => removeSpecificationRow(index)}
                              disabled={input.specification.length === 1}
                              size="small"
                              sx={{
                                color: 'error.main',
                                '&:hover': { bgcolor: 'error.light' }
                              }}
                            >
                              <Remove />
                            </IconButton>
                            {index === input.specification.length - 1 && (
                              <IconButton
                                onClick={addSpecificationRow}
                                size="small"
                                sx={{
                                  color: 'success.main',
                                  '&:hover': { bgcolor: 'success.light' }
                                }}
                              >
                                <Add />
                              </IconButton>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Keywords Section */}
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#667eea' }}>
                  Keywords
                </Typography>
                <KeywordsInput
                  label="Products Related Keywords"
                  keywords={input.keywords || []}
                  onChange={(newKeywords) => onChange({ target: { name: "keywords", value: newKeywords } })}
                />
                {keywordsError && (
                  <Typography variant="caption" color="error" sx={{ ml: 1, mt: 1, display: 'block' }}>
                    {keywordsError}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Media Upload Section */}
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#667eea' }}>
                  Media Files
                </Typography>
                <Grid container spacing={3}>
                  {/* Images */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        border: '2px dashed #667eea',
                        borderRadius: 2,
                        bgcolor: '#f8f9ff',
                        textAlign: 'center',
                        transition: 'all 0.3s',
                        '&:hover': {
                          bgcolor: '#f0f2ff',
                          borderColor: '#764ba2'
                        }
                      }}
                    >
                      <input
                        accept="image/*"
                        multiple
                        type="file"
                        id="upload-images"
                        style={{ display: "none" }}
                        onChange={handleImageSelect}
                      />
                      <label htmlFor="upload-images" style={{ cursor: 'pointer' }}>
                        <Stack spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              bgcolor: '#667eea',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 40, color: 'white' }} />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea' }}>
                            Upload Product Images *
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Min 1, Max 4 images
                          </Typography>
                          <Chip
                            label={`${images.length}/4 uploaded`}
                            color={images.length > 0 ? "success" : "default"}
                            size="small"
                          />
                        </Stack>
                      </label>
                      {imageErrors.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          {imageErrors.map((error, index) => (
                            <Typography key={index} variant="caption" color="error" sx={{ display: 'block' }}>
                              {error}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {touched?.images && images.length < 1 && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                          Please upload at least 1 image
                        </Typography>
                      )}
                      {images.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 3, justifyContent: 'center' }}>
                          {images.map((file, index) => (
                            <Box
                              key={index}
                              sx={{
                                position: 'relative',
                                width: 100,
                                height: 100,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                            >
                              <Box
                                component="img"
                                src={typeof file === 'string' ? file : URL.createObjectURL(file)}
                                alt={`preview-${index}`}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: "cover"
                                }}
                              />
                              <IconButton
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  bgcolor: 'rgba(0,0,0,0.6)',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'error.main'
                                  },
                                  width: 28,
                                  height: 28
                                }}
                                onClick={() => onRemoveImage(index)}
                              >
                                <Close sx={{ fontSize: 18 }} />
                              </IconButton>
                              <Chip
                                label={index + 1}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  left: 4,
                                  height: 20,
                                  bgcolor: 'rgba(102, 126, 234, 0.9)',
                                  color: 'white',
                                  fontSize: 10
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  {/* Videos */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        border: '2px dashed #ff9800',
                        borderRadius: 2,
                        bgcolor: '#fff8f0',
                        textAlign: 'center',
                        transition: 'all 0.3s',
                        '&:hover': {
                          bgcolor: '#fff3e0',
                          borderColor: '#f57c00'
                        }
                      }}
                    >
                      <input
                        accept="video/*"
                        multiple
                        type="file"
                        id="upload-videos"
                        style={{ display: "none" }}
                        onChange={onVideoSelect}
                      />
                      <label htmlFor="upload-videos" style={{ cursor: 'pointer' }}>
                        <Stack spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              bgcolor: '#ff9800',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1
                            }}
                          >
                            <VideoLibrary sx={{ fontSize: 40, color: 'white' }} />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ff9800' }}>
                            Upload Product Videos
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Max 2 videos
                          </Typography>
                          <Chip
                            label={`${videos.length}/2 uploaded`}
                            color={videos.length > 0 ? "success" : "default"}
                            size="small"
                          />
                        </Stack>
                      </label>
                      {videoErrors.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          {videoErrors.map((error, index) => (
                            <Typography key={index} variant="caption" color="error" sx={{ display: 'block' }}>
                              {error}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {videos.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 3, justifyContent: 'center' }}>
                          {videos.map((file, index) => (
                            <Box
                              key={index}
                              sx={{
                                position: 'relative',
                                width: 100,
                                height: 100,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                            >
                              <Box
                                component="video"
                                src={typeof file === 'string' ? file : URL.createObjectURL(file)}
                                alt={`preview-${index}`}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: "cover"
                                }}
                              />
                              <IconButton
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  bgcolor: 'rgba(0,0,0,0.6)',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'error.main'
                                  },
                                  width: 28,
                                  height: 28
                                }}
                                onClick={() => onRemoveVideo(index)}
                              >
                                <Close sx={{ fontSize: 18 }} />
                              </IconButton>
                              <Chip
                                label={index + 1}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  left: 4,
                                  height: 20,
                                  bgcolor: 'rgba(255, 152, 0, 0.9)',
                                  color: 'white',
                                  fontSize: 10
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions sx={{
          p: 3,
          bgcolor: '#f8f9fa',
          position: 'sticky',
          bottom: 0,
          borderTop: '1px solid #e0e0e0',
          zIndex: 1
        }}>
          <Button
            onClick={onClose}
            color="secondary"
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{
              fontWeight: 600,
              color: 'white !important'
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Save Product'}
          </Button>

        </DialogActions>
      </MDBox>

      {/* Batch Crop Modal */}
      <CropModalForAdd
        open={cropModalOpen}
        onClose={handleCropModalClose}
        croppingQueue={croppingQueue}
        currentIndex={currentCropIndex}
        onNavigate={handleCropNavigation}
        onSaveCurrentCrop={handleSaveCurrentCrop}
        onFinish={handleFinishCropping}
        loading={false}
        aspectRatio={800 / 950}
      />
    </Dialog>
  );
};

export default AddProductModal;
